/**
 * Puts an installed iPhone app's touch targets back under its buttons after a
 * rotation.
 *
 * Turning a home-screen app to landscape and back leaves WebKit holding a
 * stale offset on the DOCUMENT: the page is scrolled by about the status bar's
 * height. Nothing here scrolls the document - the tray is `#root`, fixed, and
 * scrolls itself - so everything is still DRAWN where it belongs, but taps are
 * hit-tested against the offset layout and land below what was aimed at (you
 * have to tap above a button to press it). Safari proper does not do it; only
 * standalone mode does, so that is the only place this runs.
 *
 * The fix is to scroll the document back to the origin once the rotation has
 * settled. iOS reports a rotation in several steps (orientationchange, then one
 * or more resizes, the last one late), so it checks after each quiet spell
 * rather than on the first event. It is a no-op whenever nothing is offset.
 *
 * `?vpdbg=1` draws the numbers involved in a corner of the screen, because the
 * question can only be answered on the handset - the same idea as `perf=1`.
 */

const SETTLE_MS = [120, 450, 1000];

function isInstalledApp(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true || !!window.matchMedia?.("(display-mode: standalone)").matches;
}

function offset(): { x: number; y: number; vvTop: number } {
  return { x: window.scrollX, y: window.scrollY, vvTop: window.visualViewport?.offsetTop ?? 0 };
}

function realign(): void {
  const { x, y, vvTop } = offset();
  if (x !== 0 || y !== 0 || vvTop !== 0) window.scrollTo(0, 0);
}

export function startRotationRealign(): void {
  const debug = /[?&]vpdbg=1\b/.test(location.href);
  if (!isInstalledApp() && !debug) return;

  let timers: number[] = [];
  const schedule = () => {
    timers.forEach((t) => window.clearTimeout(t));
    timers = SETTLE_MS.map((ms) => window.setTimeout(realign, ms));
  };
  window.addEventListener("orientationchange", schedule);
  window.addEventListener("resize", schedule);
  window.visualViewport?.addEventListener("resize", schedule);

  if (debug) showReadout();
}

/** The on-device readout: document scroll, visual viewport, and window size. */
function showReadout(): void {
  const el = document.createElement("div");
  el.style.cssText =
    "position:fixed;left:4px;bottom:4px;z-index:9999;pointer-events:none;font:11px/1.3 monospace;" +
    "background:rgba(0,0,0,.7);color:#fff;padding:3px 5px;border-radius:4px;white-space:pre";
  document.body.appendChild(el);
  let lastTap = "";
  window.addEventListener(
    "pointerdown",
    (e) => {
      const hit = document.elementFromPoint(e.clientX, e.clientY);
      lastTap = `tap ${Math.round(e.clientX)},${Math.round(e.clientY)} -> ${hit?.tagName.toLowerCase() ?? "-"}${hit?.className && typeof hit.className === "string" ? "." + hit.className.split(" ")[0] : ""}`;
    },
    true,
  );
  const tick = () => {
    const { x, y, vvTop } = offset();
    const vv = window.visualViewport;
    el.textContent =
      `scroll ${x},${y}  vvTop ${vvTop.toFixed(1)}\n` +
      `inner ${window.innerWidth}x${window.innerHeight}  vv ${vv ? `${Math.round(vv.width)}x${Math.round(vv.height)}` : "-"}\n` +
      `installed ${isInstalledApp()}\n${lastTap}`;
    requestAnimationFrame(tick);
  };
  tick();
}
